import JumboNavIdentifier from "@jumbo/components/JumboVerticalNavbar/JumboNavIdentifier";
import {ArrowWrapper} from "@jumbo/components/JumboVerticalNavbar/style";
import useJumboLayoutSidebar from "@jumbo/hooks/useJumboLayoutSidebar";
import {SIDEBAR_VIEWS} from "@jumbo/utils/constants/layout";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Popover from "@mui/material/Popover";
import {Fragment, useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";


const menuBefore = {
    left: 0,
    top: 0,
    content: `''`,
    position: "absolute",
    display: "inline-block",
    width: "4px",
    height: "100%",
    backgroundColor: "transparent"
};

const hasActiveChild = (item, pathname) =>
    item?.children?.some(
        c => (c.uri && (pathname === c.uri || pathname.startsWith(`${c.uri}/`)))
            || hasActiveChild(c, pathname)
    );

const JumboNavCollapsible = ({item, translate}) => {
    const location = useLocation();
    // defaultOpen sets the initial state. Menus with defaultOpen: false start
    // open when the current route is one of their children, and also
    // auto-expand when the route changes to one.
    const [open, setOpen] = useState(
        () => (item?.defaultOpen ?? true) || hasActiveChild(item, location.pathname)
    );
    const [anchorEl, setAnchorEl] = useState(null);
    const openPopover = Boolean(anchorEl);
    const {t} = useTranslation();

    const {sidebarOptions} = useJumboLayoutSidebar();
    const isMiniAndClosed = useMemo(() => {
        return sidebarOptions?.view === SIDEBAR_VIEWS.MINI && !sidebarOptions?.open;
    }, [sidebarOptions.view, sidebarOptions.open]);

    const handlePopoverOpen = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handlePopoverClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const isActive = hasActiveChild(item, location.pathname);

    // Only collapsed-by-default menus (defaultOpen: false) auto-expand when a
    // child route is active. Other menus keep the original template behavior.
    useEffect(() => {
        if (item?.defaultOpen === false && isActive) setOpen(true);
    }, [isActive, item?.defaultOpen]);

    const label = useMemo(() => {
        return translate ? t(item?.label) : item?.label;
    }, [item, translate, t]);

    if (!item)
        return null;

    const subMenus = (item.children && item.children.length > 0) ? item.children : null;

    return (
        <Fragment>
            <ListItemButton
                component={"li"}
                onClick={() => setOpen(!open)}
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                sx={{
                    p: theme => !isMiniAndClosed ? theme.spacing(1, 3.75) : 0,
                    borderRadius: isMiniAndClosed ? "50%" : "0 24px 24px 0",
                    margin: isMiniAndClosed ? "0 auto" : "0",
                    ...(isMiniAndClosed) ? {width: 40, height: 40, justifyContent: "center"} : {},
                    overflow: "hidden",
                    "&:hover": {
                        color: theme => theme.palette.nav.action.hover,
                        backgroundColor: theme => theme.palette.nav.background.hover,
                        ...(!isMiniAndClosed) ? {
                            "&::before": {
                                ...menuBefore,
                                backgroundColor: theme => theme.palette.nav.tick.hover
                            }
                        } : {}
                    },
                    ...(!isMiniAndClosed) ? {"&::before": menuBefore} : {}
                }}
            >
                {
                    !isMiniAndClosed &&
                    <ArrowWrapper>
                        {open ? <ArrowDropDownIcon/> : <ArrowRightIcon/>}
                    </ArrowWrapper>
                }
                {
                    item.icon &&
                    <ListItemIcon sx={{minWidth: isMiniAndClosed ? 20 : 32, color: "inherit"}}>{item.icon}</ListItemIcon>
                }
                {
                    !isMiniAndClosed &&
                    <ListItemText
                        primary={label}
                        sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }
                        }}
                    />
                }
            </ListItemButton>
            {
                subMenus && !isMiniAndClosed &&
                <Collapse component="li" in={open} timeout="auto" unmountOnExit>
                    <List disablePadding>
                        {
                            subMenus.map((child, index) => (
                                <JumboNavIdentifier item={child} key={child.uri ?? child.label ?? index} isNested={true}/>
                            ))
                        }
                    </List>
                </Collapse>
            }
            {
                subMenus && isMiniAndClosed &&
                <Popover
                    id="mouse-over-popover"
                    sx={{
                        pointerEvents: "none"
                    }}
                    open={openPopover}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left"
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                >
                    <List disablePadding>
                        {
                            subMenus.map((child, index) => (
                                <JumboNavIdentifier item={child} key={child.uri ?? child.label ?? index} isNested={true}/>
                            ))
                        }
                    </List>
                </Popover>
            }
        </Fragment>
    );
};


export default JumboNavCollapsible;